/**
 * Heat Map · one clicked area: where freight from it pays best (J2) and what
 * can be booked out of it right now (J3).
 *
 * Both are slices the module already computed — the destinations are ranked
 * inside the snapshot, the top loads are one bounded live query — so a click
 * costs a lookup, not an aggregation.
 *
 * A destination links to Lane Prices for that lane, priced from the two cell
 * centres. That is the lane picker: pick an area, pick where you would go.
 */

import Box from '@mui/joy/Box'
import Link from '@mui/joy/Link'
import Sheet from '@mui/joy/Sheet'
import Table from '@mui/joy/Table'
import Typography from '@mui/joy/Typography'
import type { MarketIntelAreaHeat, MarketIntelTier } from '@numeo/types-core'
import { cellToLatLng } from 'h3-js'
import { Link as RouterLink } from 'react-router-dom'
import { marketIntelApi } from 'api/marketIntel'
import { ErrorState, formatNumber, useLoadPipelineData } from './shared'
import { SnapshotAge, money } from './marketIntel'

/** The Lane Prices URL for a cell-to-cell lane, as two `lat,lon` points. */
function lanePricesHref(origin: string, dest: string, equipment: string): string {
    const point = (cell: string) =>
        cellToLatLng(cell)
            .map((v) => v.toFixed(4))
            .join(',')
    const params = new URLSearchParams({ o: point(origin), d: point(dest), equipment })
    return `/load-pipeline/market-intel/lane-prices?${params.toString()}`
}

function Destinations({ tier, cell, equipment }: { tier: MarketIntelTier; cell: string; equipment: string }) {
    const { data, error, loading } = useLoadPipelineData(() => marketIntelApi.getDestinations(cell, equipment, tier, 10), [tier, cell, equipment])

    if (error) return <ErrorState error={error} />
    if (loading || !data) return <Typography level="body-xs">Ranking…</Typography>
    if (data.destinations.length === 0) return <Typography level="body-xs">Not enough market data out of this area.</Typography>

    return (
        <Box sx={{ display: 'grid', gap: 0.25 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
                <Typography level="title-sm">Best-paying destinations</Typography>
                <SnapshotAge provenance={data} />
            </Box>
            {data.destinations.map((d) => (
                <Box key={d.cell} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Link component={RouterLink} to={lanePricesHref(cell, d.cell, data.equipment)} level="body-xs" sx={{ fontFamily: 'monospace' }}>
                        {d.cell.slice(0, 9)}… → price lane
                    </Link>
                    <Typography level="body-xs">{money(d.avgRpm)}/mi</Typography>
                </Box>
            ))}
        </Box>
    )
}

function place(city: string | null, state: string | null): string {
    return [city, state].filter(Boolean).join(', ') || '—'
}

function TopLoads({ cell, equipment }: { cell: string; equipment: string }) {
    const { data, error, loading } = useLoadPipelineData(() => marketIntelApi.getTopLoads(cell, equipment, 10), [cell, equipment])

    if (error) return <ErrorState error={error} />
    if (loading || !data) return <Typography level="body-xs">Loading open loads…</Typography>

    return (
        <Box>
            <Typography level="title-sm">Book now — open loads by $/mi</Typography>
            <Typography level="body-xs" sx={{ color: 'text.tertiary', mb: 0.5 }}>
                Live, as of {new Date(data.asOf).toLocaleTimeString()}
            </Typography>
            {data.loads.length === 0 ? (
                <Typography level="body-xs">No open loads out of this area right now.</Typography>
            ) : (
                <Sheet variant="outlined" sx={{ borderRadius: 'sm', overflow: 'auto', maxHeight: 320 }}>
                    <Table size="sm" stickyHeader>
                        <thead>
                            <tr>
                                <th>Lane</th>
                                <th style={{ width: 70 }}>$/mi</th>
                                <th style={{ width: 70 }}>Rate</th>
                            </tr>
                        </thead>
                        <tbody>
                            {data.loads.map((l) => (
                                <tr key={l.externalId}>
                                    <td>
                                        <Typography level="body-xs">
                                            {place(l.originCity, l.originState)} → {place(l.destinationCity, l.destinationState)}
                                        </Typography>
                                        <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                                            {l.equipment ?? '—'} · {l.miles == null ? '—' : `${formatNumber(Math.round(l.miles))} mi`}
                                            {l.pickupDate ? ` · pickup ${new Date(l.pickupDate).toLocaleDateString()}` : ''}
                                        </Typography>
                                    </td>
                                    <td>{money(l.ratePerMile)}</td>
                                    <td>{l.rate == null ? '—' : `$${formatNumber(Math.round(l.rate))}`}</td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </Sheet>
            )}
        </Box>
    )
}

export function AreaPanel({ area, tier, equipment, onClear }: { area: MarketIntelAreaHeat; tier: MarketIntelTier; equipment: string; onClear: () => void }) {
    return (
        <Box sx={{ display: 'grid', gap: 1.5 }}>
            <Box sx={{ display: 'grid', gap: 0.5 }}>
                <Typography level="body-xs" sx={{ fontFamily: 'monospace' }}>
                    {area.cell}
                </Typography>
                <Typography level="body-sm">{formatNumber(area.observations)} loads in the window</Typography>
                <Typography level="body-sm">
                    median {area.p50 == null ? 'not enough market data' : `${money(area.p50)}/mi`} · avg {money(area.avgRpm)}/mi
                </Typography>
                <Typography level="body-xs" sx={{ cursor: 'pointer', textDecoration: 'underline' }} onClick={onClear}>
                    clear
                </Typography>
            </Box>
            <Destinations tier={tier} cell={area.cell} equipment={equipment} />
            <TopLoads cell={area.cell} equipment={equipment} />
        </Box>
    )
}
